import watermarkLogo from '../assets/images/logo.webp';

const DB_NAME = 'vto_db';
const DB_VERSION = 1;
const STORE = 'video_clips';

function openDb() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'id' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function txp(db, mode) {
  const tx = db.transaction(STORE, mode);
  const store = tx.objectStore(STORE);
  return { tx, store };
}

export async function loadVideoClips() {
  try {
    const db = await openDb();
    const { store } = txp(db, 'readonly');
    return await new Promise((resolve, reject) => {
      const req = store.getAll();
      req.onsuccess = () => {
        const arr = Array.isArray(req.result) ? req.result : [];
        // newest first
        arr.sort((a, b) => (b?.ts || 0) - (a?.ts || 0));
        resolve(arr);
      };
      req.onerror = () => reject(req.error);
    });
  } catch {
    return [];
  }
}

export async function saveVideoClip(clip) {
  const db = await openDb();
  const { store } = txp(db, 'readwrite');
  return await new Promise((resolve, reject) => {
    const req = store.put(clip);
    req.onsuccess = () => resolve(clip);
    req.onerror = () => reject(req.error);
  });
}

export async function deleteVideoClip(id) {
  const db = await openDb();
  const { store } = txp(db, 'readwrite');
  return await new Promise((resolve, reject) => {
    const req = store.delete(id);
    req.onsuccess = () => resolve(true);
    req.onerror = () => reject(req.error);
  });
}

export async function clearVideoClips() {
  const db = await openDb();
  const { store } = txp(db, 'readwrite');
  return await new Promise((resolve, reject) => {
    const req = store.clear();
    req.onsuccess = () => resolve(true);
    req.onerror = () => reject(req.error);
  });
}

function uid() {
  return `${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

function sleep(ms) {
  return new Promise((r) => setTimeout(r, ms));
}

// Draw logo/text watermark inside a 2d canvas.
let _wmImg = null;
function getWatermarkImg() {
  if (typeof Image === 'undefined') return null;
  if (_wmImg) return _wmImg;
  const img = new Image();
  img.src = watermarkLogo;
  _wmImg = img;
  return img;
}

export function drawMojeNaocaleWatermark(ctx, w, h, opts = {}) {
  if (!ctx || !w || !h) return;
  const opacity = typeof opts.opacity === 'number' ? opts.opacity : 0.6;
  const padding = typeof opts.padding === 'number' ? opts.padding : Math.round(Math.min(w, h) * 0.03);
  const targetH = typeof opts.height === 'number' ? opts.height : Math.round(h * 0.035);

  const img = getWatermarkImg();
  ctx.save();
  ctx.globalAlpha = opacity;

  if (img && img.complete && img.naturalWidth > 0 && img.naturalHeight > 0) {
    const aspect = img.naturalWidth / img.naturalHeight;
    const drawH = Math.max(18, Math.min(targetH, 64));
    const drawW = Math.round(drawH * aspect);
    const x = w - padding - drawW;
    const y = h - padding - drawH;
    ctx.drawImage(img, x, y, drawW, drawH);
  } else {
    const text = 'visionlive';
    ctx.font = `600 ${Math.max(12, Math.round(h * 0.022))}px "Google Sans", Inter, system-ui, -apple-system, Segoe UI, sans-serif`;
    ctx.textBaseline = 'bottom';
    ctx.fillStyle = 'rgba(255,255,255,0.9)';
    const metrics = ctx.measureText(text);
    const x = w - padding - metrics.width;
    const y = h - padding;
    ctx.shadowColor = 'rgba(0,0,0,0.5)';
    ctx.shadowBlur = 6;
    ctx.fillText(text, x, y);
  }

  ctx.restore();
}

/**
 * Snimi kratki "pose clip": base frame (video cover) + pose po frameu.
 * Frames su JPEG blobovi (za IndexedDB), pose su mali JSON.
 */
export async function recordPoseClip({
  stageEl,
  videoEl,
  getPose,
  durationMs = 6000,
  fps = 12,
  mirrored = true,
  scale = 0.7,
  jpegQuality = 0.75,
}) {
  if (!stageEl || !videoEl) throw new Error('stageEl/videoEl missing');
  if (typeof getPose !== 'function') throw new Error('getPose missing');

  const baseW = Math.round(stageEl.clientWidth || 800);
  const baseH = Math.round(stageEl.clientHeight || 600);
  const s = Math.max(0.35, Math.min(1, Number(scale) || 1));
  const w = Math.max(2, Math.round(baseW * s));
  const h = Math.max(2, Math.round(baseH * s));

  const out = document.createElement('canvas');
  out.width = w;
  out.height = h;
  const ctx = out.getContext('2d');
  if (!ctx) throw new Error('2d context missing');

  const frames = [];
  const poses = [];
  const safeFps = Math.max(6, Math.min(20, Math.round(Number(fps) || 12)));
  const interval = 1000 / safeFps;
  const totalFrames = Math.max(1, Math.round((durationMs / 1000) * safeFps));

  const drawCover = () => {
    const vw = videoEl.videoWidth || w;
    const vh = videoEl.videoHeight || h;
    const scale = Math.max(w / vw, h / vh);
    const dw = vw * scale;
    const dh = vh * scale;
    const dx = (w - dw) / 2;
    const dy = (h - dh) / 2;

    ctx.save();
    ctx.clearRect(0, 0, w, h);
    if (mirrored) {
      ctx.translate(w, 0);
      ctx.scale(-1, 1);
      ctx.drawImage(videoEl, dx, dy, dw, dh);
    } else {
      ctx.drawImage(videoEl, dx, dy, dw, dh);
    }
    ctx.restore();
  };

  for (let i = 0; i < totalFrames; i += 1) {
    const t0 = performance.now();
    drawCover();
    poses.push(getPose() || null);
    // eslint-disable-next-line no-await-in-loop
    const blob = await new Promise((resolve) => out.toBlob(resolve, 'image/jpeg', jpegQuality));
    frames.push(blob);
    const elapsed = performance.now() - t0;
    const remaining = interval - elapsed;
    if (remaining > 0) {
      // eslint-disable-next-line no-await-in-loop
      await sleep(remaining);
    }
  }

  const firstFrame = frames[0] || null;
  const clip = {
    id: uid(),
    ts: Date.now(),
    v: 1,
    durationMs: Math.round((totalFrames / safeFps) * 1000),
    fps: safeFps,
    stage: { w, h },
    mirrored,
    frames, // Blob[]
    poses, // object[]
    firstFrame, // Blob
  };

  return clip;
}

export async function blobToDataUrl(blob) {
  if (!blob) return null;
  return await new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = () => reject(r.error);
    r.readAsDataURL(blob);
  });
}

export function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename || 'video.webm';
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}


